import React, { useEffect, useState } from "react";
import OverallStatus from "./OverallStatus";
import MachineHealth from "./MachinesHealth";
import LatestTrans from "./LatestTrans";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "../components/css/RealtimeStatus.css";

const RealtimeStatus = () => {

    const auth = getAuth();
    const navigate = useNavigate();

    const [onlineData, setOnlineData] = useState({ onlinePercentage: 0, onlineVal: "" });
    const [stockData, setStockData] = useState({ stockPercentage: 0, stockVal: "" });
    const [errorData, setErrorData] = useState({ errorPercentage: 0, errorVal: "" });
    const [machinesDetails, setMachinesDetails] = useState([]);
    const [latestTransactions, setLatestTransaction] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    //get machines status and latest transaction
    const getMachineStatusAndTrans = async () => {
        try {
            let token =  await waitForToken();

            if(!token) {
                throw new Error("No token found in local storage");
            }

            const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/machine_transaction/machinesStatus`, {
                params: {
                    status: 1,
                    limit: 5,
                },
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                }
            });

            const data = res.data?.result?.data ?? { onlinePercentage: 0, stock: 0, health: 0, machinesDetail: [] };

            data.machinesDetail = data.machinesDetail.map(machine => ({
                ...machine,
                timeAgo: machine.date ? timeAgo(machine.date) : "N/A"
            }));
            
            return data;
        } catch (err) {
            console.log('Error fetching data:', err);
            return { onlinePercentage: 0, stock: 0, health: 0, machinesDetail: [] };
        }
    };

    //get latest transaction with desc order
    const getLatestTrans = async () => {
        try {
            let token =  await waitForToken();

            const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/machine_transaction/latestTrans`, {
                params: {
                    status: 1, //success
                    limit: 5, //select top 5
                },
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                }
            });

            return res.data.result.data;
        } catch (error) {
            console.log('Error fetching data:', error);
        }
    };

    const waitForToken = async (timeout = 5000) => {
        return new Promise((resolve, reject) => {
            let timeElapsed = 0;
            const interval = 200; // Check every 200ms
            
            const checkToken = setInterval(() => {
                const token = localStorage.getItem("token");
                if (token) {
                    clearInterval(checkToken);
                    resolve(token);
                }
                timeElapsed += interval;
                if (timeElapsed >= timeout) {
                    clearInterval(checkToken);
                    reject("Token not found within timeout!");
                }
            }, interval);
        });
    };

    const timeAgo = (date) => {
        if(!date) return "N/A";

        const now = new Date();
        const past = new Date(date);
        const diffInSec = Math.floor((now.getTime() - past.getTime()) / 1000);

        if (diffInSec < 60) return `${diffInSec} seconds ago`;
        if (diffInSec < 3600) return `${Math.floor(diffInSec / 60)} minutes ago`;
        if (diffInSec < 86400) return `${Math.floor(diffInSec / 3600)} hours ago`;
        if (diffInSec < 2592000) return `${Math.floor(diffInSec / 86400)} days ago`;
        if (diffInSec < 604800) return `${Math.floor(diffInSec / 604800)} weeks ago`;

        const currentYear = now.getFullYear();
        const pastYear = past.getFullYear();
        const yearDiff = currentYear - pastYear;
        const monthDiff = now.getMonth() - past.getMonth() + (yearDiff * 12);

        if(yearDiff === 0) return `${monthDiff} months ago`;
        return `${yearDiff} years ago`;
    };

    useEffect(() => {
        const checkAuth = onAuthStateChanged(auth, async (user) => {
            if(user) {
                setIsLoading(true);
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    const role = idTokenResult.claims.role;

                    const latestTransaction = await getLatestTrans();
                    setLatestTransaction(latestTransaction);

                    if(role !== "admin") {
                        const datas = await getMachineStatusAndTrans();
    
                        const online = {
                            onlinePercentage: datas.onlinePercentage ?? 0,
                            onlineVal: datas.onlineMachines !== undefined && datas.totalMachines !== undefined 
                                ? `${datas.onlineMachines}/${datas.totalMachines}`
                                : "0/0"
                        };
    
                        const health = {
                            errorPercentage: (100 - datas.health) ?? 0,
                            errorVal: datas.slotError !== undefined && datas.totalSlot !== undefined 
                                ? `${datas.slotError}/${datas.totalSlot}`
                                : "0/0"
                        };
    
                        const stock = {
                            stockPercentage: datas.stock,
                            stockVal: `${datas.stock}%`,
                        };
                        
                        setOnlineData(online);
                        setStockData(stock);
                        setErrorData(health);
                        setMachinesDetails(datas.machinesDetail);
                    }
                } catch (error) {
                    console.log('Error fetching data:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                navigate("/");
            }
        });

        return () => checkAuth();
    }, []);

    return(
        <>
            <div className="realtimeArea">
                <h4 className="lblRealtime">Realtime Status</h4>
                <div className="overallDiv">
                    <OverallStatus onlineData={onlineData} stockData={stockData} errorData={errorData} />
                </div>
                <div className="machineHealthDiv">
                    <MachineHealth machinesDetails={machinesDetails} isLoading={isLoading} />
                </div>
                <div className="latest-trans-div">
                    <LatestTrans latestTransactions={latestTransactions} isLoading={isLoading} />
                </div>
            </div>
        </>
    );
};

export default RealtimeStatus;